/**
 * @fileoverview NameField component for editing metadata item names.
 * Provides a controlled input field that can be disabled when linked to existing items.
 */

import { useTranslation } from "@/i18n";

/**
 * Props for the NameField component.
 */
interface NameFieldProps {
  /** Current value of the name field */
  readonly value: string;
  /** Callback when the value changes */
  readonly onChange: (value: string) => void;
  /** Whether the field is disabled (typically when linked to existing item) */
  readonly disabled: boolean;
  /** Whether the field should receive focus on mount */
  readonly autoFocus: boolean;
}

/**
 * NameField provides a text input for editing metadata item names.
 * The field is disabled when the item is linked to an existing AIIO element.
 *
 * @param props - Component props
 * @returns Name input field UI
 */
export default function NameField({
  value,
  onChange,
  disabled,
  autoFocus,
}: NameFieldProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-2">
      <label className="text-sm text-[#18181b] leading-5">{t("documentReview.modal.name")}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`w-full h-10 bg-[#fafafa] rounded-md px-4 py-1 text-sm text-[#71717a] focus:outline-none focus:ring-1 focus:ring-[#d4d4d8] ${
          disabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
        placeholder={t("documentReview.modal.namePlaceholder")}
        autoFocus={autoFocus}
      />
    </div>
  );
}
